import type { UserSession } from '@shared/types';
import type { AppPreferences } from './platform/types';

export const SESSION_INACTIVITY_LIMIT_MS = 30 * 24 * 60 * 60 * 1000;
export const SESSION_ACTIVITY_WRITE_INTERVAL_MS = 6 * 60 * 60 * 1000;

export type StoredSessionDecision =
  | { action: 'none' }
  | { action: 'restore'; reason: 'active' | 'legacy' }
  | { action: 'expire'; lastActivityAt: string };

export function getLastSessionActivityMs(
  preferences: AppPreferences | null | undefined,
): number | null {
  const rawValue = preferences?.lastSessionActivityAt;
  if (typeof rawValue !== 'string' || !rawValue.trim()) {
    return null;
  }

  const timestampMs = Date.parse(rawValue);
  return Number.isFinite(timestampMs) ? timestampMs : null;
}

export function resolveStoredSessionDecision(
  storedSession: UserSession | null,
  preferences: AppPreferences | null | undefined,
  nowMs: number = Date.now(),
): StoredSessionDecision {
  if (!storedSession) {
    return { action: 'none' };
  }

  const lastActivityMs = getLastSessionActivityMs(preferences);
  if (lastActivityMs == null) {
    // Existing users won’t have activity metadata yet. Keep them signed in
    // and start tracking visits from the next successful restore.
    return { action: 'restore', reason: 'legacy' };
  }

  if (nowMs - lastActivityMs >= SESSION_INACTIVITY_LIMIT_MS) {
    return {
      action: 'expire',
      lastActivityAt: new Date(lastActivityMs).toISOString(),
    };
  }

  return { action: 'restore', reason: 'active' };
}

export function withSessionActivity(
  preferences: AppPreferences | null | undefined,
  nowMs: number = Date.now(),
  options: { force?: boolean } = {},
): AppPreferences {
  const currentPreferences = preferences ?? {};
  const lastActivityMs = getLastSessionActivityMs(currentPreferences);

  if (
    !options.force
    && lastActivityMs != null
    && nowMs - lastActivityMs < SESSION_ACTIVITY_WRITE_INTERVAL_MS
  ) {
    return currentPreferences;
  }

  return {
    ...currentPreferences,
    lastSessionActivityAt: new Date(nowMs).toISOString(),
  };
}
